import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllAllergens,
  getSingleAllergenById,
  createAllergen,
  updateAllergen,
  deleteAllergen,
} from "@/api/new-api/staff-management/menu-manage/essential-menu/allergen";

// Thunks

export const fetchAllAllergens = createAsyncThunk(
  "allergens/fetchAll",
  async ({ restaurant_id, menu_id }, { rejectWithValue }) => {
    const res = await getAllAllergens(restaurant_id, menu_id);
    if (res.status === "success") return res.data.allergens;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleAllergen = createAsyncThunk(
  "allergens/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleAllergenById(id);
    if (res.status === "success") return res.data.allergen;
    return rejectWithValue(res.message);
  }
);

export const createNewAllergen = createAsyncThunk(
  "allergens/create",
  async (data, { rejectWithValue }) => {
    const res = await createAllergen(data);
    if (res.status === "success") return res.data.allergen;
    return rejectWithValue(res.message);
  }
);

export const updateExistingAllergen = createAsyncThunk(
  "allergens/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateAllergen(id, data);
    if (res.status === "success") return res.data.allergen;
    return rejectWithValue(res.message);
  }
);

export const deleteAllergenById = createAsyncThunk(
  "allergens/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteAllergen(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  allergens: [],
  singleAllergen: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const allergenSlice = createSlice({
  name: "allergens",
  initialState,
  reducers: {
    clearAllergenMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllAllergens.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllAllergens.fulfilled, (state, action) => {
        state.getLoading = false;
        state.allergens = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllAllergens.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleAllergen.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleAllergen.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleAllergen = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleAllergen.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewAllergen.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewAllergen.fulfilled, (state, action) => {
        state.createLoading = false;
        state.allergens.push(action.payload);
        state.success = "Allergen created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewAllergen.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingAllergen.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingAllergen.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.allergens = state.allergens.map((a) =>
          a.id === action.payload.id ? action.payload : a
        );
        state.success = "Allergen updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingAllergen.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteAllergenById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteAllergenById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.allergens = state.allergens.map((item) =>
          item.id === action.payload ? { ...item, status: "archive" } : item
        );
        state.success = "Allergen deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteAllergenById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

// Export actions and reducer
export const { clearAllergenMessages } = allergenSlice.actions;
export default allergenSlice.reducer;
